import React, {
	type ComponentType,
	Fragment,
	type PropsWithChildren,
	useCallback,
	useMemo,
} from 'react';

import { FormattedMessage } from 'react-intl-next';

import SearchIcon from '@atlaskit/icon/core/search';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Anchor, Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
	type ExtensionMode,
	type ExtensionParameters,
	HubExtensionsWrapper,
	type ProductDependenciesProviderProps,
	useGetSpaceIdsFromSpaceKeys,
	useProductDependencies,
} from '@atlassian/company-hub-common';
import {
	type QueryParams,
	SearchInput as SearchInputWithStore,
	type UserDetails,
} from '@atlassian/dynamic-cards-search-page';
import { RelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import type { SearchExtensionParameters } from '../../types';

import { messages } from './messages';

const containerBaseStyles = xcss({
	width: '100%',
	height: `${token('space.600')}`,
	borderTop: `${token('border.width')} solid ${token('color.border')}`,
});

// Search dropdown has a padding. This is a workaround for placing the footer on the right spot until we handle this in the search component.
const layoutStyles = xcss({
	marginLeft: 'space.negative.100',
	marginTop: 'space.100',
	marginBottom: 'space.negative.100',
	width: `calc(100% + 16px)`,
});

const anchorStyles = xcss({
	width: '100%',
	height: '100%',
	color: 'color.text',
	backgroundColor: 'elevation.surface',
	textDecoration: 'none',
	display: 'inline-flex',
	alignItems: 'center',
	gap: 'space.150',
	paddingInline: 'space.250',
	':hover': {
		backgroundColor: 'elevation.surface.hovered',
		color: 'color.text.subtle',
		textDecoration: 'none',
	},
});

export const SearchFooter = ({ searchURL }: { searchURL: string }): React.JSX.Element => (
	<Box xcss={[containerBaseStyles, layoutStyles]}>
		<Anchor href={searchURL} xcss={anchorStyles}>
			<SearchIcon label="" />
			<FormattedMessage {...messages.moreButton} />
		</Anchor>
	</Box>
);

export type CustomSearchExtensionPropsProviderProps = PropsWithChildren<{
	mode: ExtensionMode;
}> &
	ExtensionParameters<SearchExtensionParameters>;

export type CustomSearchExtensionProps = ExtensionParameters<SearchExtensionParameters> & {
	propsProvider: ComponentType<ProductDependenciesProviderProps>;
} & Pick<CustomSearchExtensionPropsProviderProps, 'mode'>;

const CustomSearchExtensionInner = ({
	extensionNode,
	contentId,
	mode,
}: Omit<CustomSearchExtensionProps, 'propsProvider'>) => {
	const {
		searchConfigProvider,
		cloudId,
		displayName,
		isAdminHubAIEnabled,
		orgId,
		userId,
		experienceSuccessComponent,
	} = useProductDependencies();

	const { peopleDirectoryUrl, advancedSearchUrl } = searchConfigProvider || {};

	const getSpaceIdsFromSpaceKeys = useGetSpaceIdsFromSpaceKeys();

	const { parameters } = extensionNode || {};

	const userDetails = useMemo<UserDetails>(() => {
		return {
			id: userId ?? null,
			name: displayName ?? null,
		};
	}, [userId, displayName]);

	const mapUserIdToProfileUrl = useCallback(
		(userId: string) => `${peopleDirectoryUrl}/${userId}`,
		[peopleDirectoryUrl],
	);

	const generateSearchUrl = useCallback(
		(queryParams: QueryParams) => {
			const url = new URL(advancedSearchUrl ?? '', window.location.origin);
			Object.entries(queryParams).forEach(([key, value]) => {
				if (value !== undefined && value !== null) {
					url.searchParams.set(key, String(value));
				}
			});
			return url.href;
		},
		[advancedSearchUrl],
	);

	const ExperienceSuccessComponent = experienceSuccessComponent || Fragment;

	return (
		<HubExtensionsWrapper<SearchExtensionParameters>
			extensionNode={extensionNode}
			contentId={contentId}
			mode={mode}
			packageName="custom-search-extension"
		>
			<SearchInputWithStore
				shell={{
					cloudId,
					orgId,
					user: userDetails,
					primaryProduct: 'confluence' as const,
					queryParams: parameters?.filters,
					isAdminHubAIEnabled,
					styleOverrides: {
						textfield: parameters?.styles,
					},
					components: {
						footer: (searchURL: string) => <SearchFooter searchURL={searchURL} />,
					},
					features: {
						// This macro will be used to do quick searches based on provided filters and query
						quickSearch: { enabled: true },
						// Inline and recent search items are tied to the query params. Disabling them until we decouple.
						inlineSearch: { enabled: false },
						// Recent search items are tied to the query params. Disabling them until we decouple.
						recentSearches: { enabled: false },
						// People and teams search is not included in the requirements. Disabling it until requested.
						peopleAndTeamsSearch: { enabled: false },
						// We can enable this again once we confirm it works correctly with "quickSearch."
						recentViews: { enabled: false },
						// This is disabled for Confluence in the code. We're making sure it's disabled via config as well
						autoSuggestion: { enabled: false },
						// We'll only use the search input in this shell instance, no need for page related server calls
						fetchInitialResults: { enabled: false },
						// To disable changeboarding spotlights
						engagement: { enabled: false },
					},
				}}
				callbacks={{
					getSpaceIdsFromSpaceKeys,
				}}
				mapUserIdToProfileUrl={mapUserIdToProfileUrl}
				generateSearchUrl={generateSearchUrl}
			/>
			<ExperienceSuccessComponent />
		</HubExtensionsWrapper>
	);
};

export const CustomSearchExtension = ({
	propsProvider: PropsProvider,
	...props
}: CustomSearchExtensionProps): React.JSX.Element => (
	<PropsProvider {...props}>
		<RelayEnvironmentProvider>
			<CustomSearchExtensionInner {...props} />
		</RelayEnvironmentProvider>
	</PropsProvider>
);
